import React from "react";
import { formatDate } from "@/lib/utils";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

export interface PlateHandoverPrintTemplateProps {
  data?: any;
  plate?: any;
  config?: any;
}

export default function PlateHandoverPrintTemplate({
  data,
  plate,
  config,
}: PlateHandoverPrintTemplateProps) {
  const d = data || plate || {};
  const c = getCompanyConfig(config);
  const docId = d?.plateId || d?.code || d?.id || "";

  // Danh sách các màu kẽm
  const rawColors = d.colors || [];
  const colorNames = Array.isArray(rawColors) && rawColors.length > 0
    ? rawColors
    : ["Cyan (C)", "Magenta (M)", "Yellow (Y)", "Black (K)"];

  const angles = [15, 75, 90, 45, 45, 45];

  return (
    <div
      className="kh-print-doc bg-white w-full mx-auto p-[5mm] text-black"
      style={{
        fontFamily: "'Times New Roman', Times, serif",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
      id="print-area"
    >
      {/* 1. Header Công ty ISO 9001 */}
      <ISOPrintHeader config={config} className="mb-3" />

      {/* 2. Tiêu đề biểu mẫu */}
      <PrintDocumentTitle
        title="PHIẾU BÀN GIAO BẢN KẼM CTP & THEO DÕI MÁY IN OFFSET"
        subtitle="CTP PLATE DISPATCH & OFFSET MACHINE HANDOVER NOTE"
        docNo={docId}
        date={d.mountedAt || d.createdAt || new Date()}
        dateLabel="Ngày cấp phát"
      />

      {/* 3. Thông tin bài in & Máy in nhận kẽm */}
      <div className="mb-4">
        <PrintInfoGrid
          labelWidth="w-[130px]"
          leftColumn={[
            {
              label: "Tên bài in / SP",
              value: d.productName || d.product?.productName || d.name || "—",
              isBold: true,
            },
            {
              label: "Khách hàng",
              value: d.customerName || d.customer?.name || d.product?.customer?.name || "Nội bộ APV",
            },
            {
              label: "Kích thước bản kẽm",
              value: d.size || "Theo khổ máy in",
              isBold: true,
            },
            {
              label: "Duyệt Mẫu Proof",
              value: `${d.proofStatus === "APPROVED" ? "✓ ĐÃ KÝ DUYỆT ĐẠT" : d.proofStatus === "REJECTED" ? "✗ TỪ CHỐI" : "CHỜ DUYỆT"}${d.proofApprover ? ` (${d.proofApprover})` : ""}`,
              highlight: d.proofStatus === "APPROVED",
            },
          ]}
          rightColumn={[
            {
              label: "Mã bài kẽm CTP",
              value: docId,
              isBold: true,
              highlight: true,
            },
            {
              label: "Máy in Offset đích",
              value: d.activeMachineName || d.machineName || "Máy In Offset APV",
              isBold: true,
            },
            {
              label: "Thợ trưởng máy",
              value: d.mountedBy || "Tổ Trưởng Máy In",
              isBold: true,
            },
            {
              label: "Trạng thái kẽm",
              value: d.status === "IN_USE" ? "ĐANG LẮP TRÊN MÁY" : d.status === "AVAILABLE" ? "SẴN SÀNG" : d.status || "—",
            },
          ]}
        />
      </div>

      {/* 4. Bảng chi tiết phân màu bộ kẽm */}
      <div className="mb-4">
        <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase flex justify-between items-center">
          <span>1. CHI TIẾT TỪNG LÁ KẼM & NGHIỆM THU BÀN GIAO MÁY IN</span>
          <span className="text-[10px] lowercase font-normal italic">
            (Kiểm tra độ sạch tram, không xước cát, ngàm kẽm phẳng phiu)
          </span>
        </div>
        <table className="w-full border-collapse border border-black border-t-0 text-[11px] font-sans">
          <thead>
            <tr className="bg-slate-50 border-b border-black text-slate-900 font-bold">
              <th className="border-r border-black py-1.5 px-2 text-center w-10">STT</th>
              <th className="border-r border-black py-1.5 px-2 text-left min-w-[140px]">Tên phân màu</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-20">Góc tram</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-24">Tần số tram</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-28">Nghiệm thu CTP</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-32">Thợ máy ký nhận</th>
              <th className="py-1.5 px-2 text-left">Ghi chú kiểm tra</th>
            </tr>
          </thead>
          <tbody>
            {colorNames.map((colorName: string, idx: number) => (
              <tr key={idx} className="border-b border-black last:border-b-0 h-8">
                <td className="border-r border-black text-center font-mono">{idx + 1}</td>
                <td className="border-r border-black px-2 font-bold uppercase">{colorName}</td>
                <td className="border-r border-black text-center font-mono">{angles[idx % angles.length]}°</td>
                <td className="border-r border-black text-center font-mono">175 LPI</td>
                <td className="border-r border-black px-2 text-center text-[10px] font-semibold text-emerald-800">
                  [ ✓ ] ĐẠT CHUẨN
                </td>
                <td className="border-r border-black px-2 text-center text-[10px] text-slate-500 italic">
                  Đã nhận đủ
                </td>
                <td className="px-2 text-slate-700 text-[10.5px]">
                  {d.damageReason && d.damagedColors?.includes(colorName)
                    ? `⚠️ Đã ghi bù: ${d.damageReason}`
                    : "Lá kẽm sắc nét, sẵn sàng lắp máy"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 5. Ghi nhận sự cố & Lịch sử ghi bù kẽm (nếu có) */}
      {d.remakeCount > 0 && (
        <div className="mb-4">
          <div className="bg-rose-50 font-bold text-[12px] px-2 py-1 border border-black uppercase text-rose-900 flex justify-between items-center">
            <span>2. NHẬT KÝ SỰ CỐ & GHI BÙ KẼM (PLATE REMAKE INCIDENTS)</span>
            <span className="text-[10px] lowercase font-normal italic">
              (Hao hụt ghi nhận chi phí phân xưởng)
            </span>
          </div>
          <table className="w-full border-collapse border border-black border-t-0 text-[11px] font-sans">
            <thead>
              <tr className="bg-rose-100/50 border-b border-black text-rose-950 font-bold">
                <th className="border-r border-black py-1 px-2 text-center w-12">Lần</th>
                <th className="border-r border-black py-1 px-2 text-left w-36">Màu bị sự cố</th>
                <th className="border-r border-black py-1 px-2 text-left">Lý do hỏng kẽm & Chi tiết</th>
                <th className="border-r border-black py-1 px-2 text-center w-24">Số lượng bù</th>
                <th className="py-1 px-2 text-center w-32">Xác nhận Tổ CTP</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-black last:border-b-0 h-7">
                <td className="border-r border-black text-center font-mono">1</td>
                <td className="border-r border-black px-2 font-semibold text-rose-700">
                  {d.damagedColors?.join(", ") || "Phân màu báo hỏng"}
                </td>
                <td className="border-r border-black px-2">
                  {d.damageReason || "Sự cố kẽm trong quá trình chạy máy in"}
                </td>
                <td className="border-r border-black text-center font-mono font-bold text-rose-800">
                  {d.remakeCount} lá
                </td>
                <td className="text-center font-medium text-slate-700">Đã xuất bù</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* 6. Ghi chú kỹ thuật */}
      <div className="mb-4">
        <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase">
          {d.remakeCount > 0 ? "3" : "2"}. QUY ĐỊNH BẢO QUẢN & THAO TÁC LẮP MÁY
        </div>
        <div className="border border-black border-t-0 p-2 text-[11px] font-sans leading-relaxed text-slate-800">
          <p>
            • <strong>Thợ máy in:</strong> Kiểm tra kỹ mặt sau lá kẽm, lau sạch trục kẽm trước khi xiết ngàm. Xiết đều tay chống cong vênh mép kẽm.
          </p>
          <p>
            • <strong>KCS/Tổ CTP:</strong> Căn chỉnh chuẩn góc tram và độ dày mực in thử, lưu mẫu Proof có chữ ký đối chứng khi giao nhận thành phẩm.
          </p>
        </div>
      </div>

      {/* 7. Khối 4 chữ ký trách nhiệm ISO 9001 */}
      <div className="grid grid-cols-4 gap-2 text-center mt-6 break-inside-avoid text-[12px] font-sans">
        <div>
          <div className="font-bold uppercase">KỸ THUẬT GHI CTP</div>
          <div className="italic text-slate-500 text-[11px] mb-12">(Ký & ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-900">{d.proofApprover || "Kỹ thuật CTP"}</div>
        </div>
        <div>
          <div className="font-bold uppercase">KCS DUYỆT PROOF</div>
          <div className="italic text-slate-500 text-[11px] mb-12">(Ký & ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-900">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">TRƯỞNG MÁY IN OFFSET</div>
          <div className="italic text-slate-500 text-[11px] mb-12">(Nhận kẽm & kiểm tra)</div>
          <div className="font-semibold text-slate-900">{d.mountedBy || "Thợ trưởng máy"}</div>
        </div>
        <div>
          <div className="font-bold uppercase">QUẢN ĐỐC PHÂN XƯỞNG</div>
          <div className="italic text-slate-500 text-[11px] mb-12">(Phê duyệt & lưu hồ sơ)</div>
          <div className="font-semibold text-slate-900">&nbsp;</div>
        </div>
      </div>
    </div>
  );
}
